import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Sparkles } from "lucide-react";
import {
	Button,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Spinner,
} from "reactstrap";
import { monitoringAnalysisStrategyReturnPath } from "../../auditUtils";
import {
	createMonitoringWritingAssistantRun,
	getMonitoringPromptAnalysis,
	listMonitoringWritingAssistantPersonas,
} from "../../../../services/monitoringApi";

const SETTINGS_PAGE = "/aivisibility/writing-assistance/settings";

export default function WritingAssistantModal({
	isOpen,
	onClose,
	onCreated,
	websiteId,
	analysisId,
	recommendationRef,
	mode,
	targetUrl,
	title,
}) {
	const navigate = useNavigate();
	const [personas, setPersonas] = useState([]);
	const [personaId, setPersonaId] = useState("");
	const [lockedPersonaId, setLockedPersonaId] = useState(0);
	const [lockedPersonaName, setLockedPersonaName] = useState("");
	const [loading, setLoading] = useState(false);
	const [creating, setCreating] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		if (!isOpen) {
			setPersonaId("");
			setLockedPersonaId(0);
			setLockedPersonaName("");
			setError("");
			return undefined;
		}
		let cancelled = false;
		setLoading(true);
		setError("");
		Promise.all([
			listMonitoringWritingAssistantPersonas(websiteId),
			getMonitoringPromptAnalysis(websiteId, analysisId),
		]).then(([personasRes, analysisRes]) => {
			if (cancelled) return;
			setLoading(false);
			if (!personasRes.ok) {
				setPersonas([]);
				setError(personasRes.message || "Could not load personas.");
			} else {
				setPersonas(personasRes.personas);
			}
			const analysis = analysisRes.ok ? analysisRes.analysis : null;
			const pid = Number(analysis?.persona_id ?? analysis?.personaId ?? 0);
			setLockedPersonaId(pid);
			setLockedPersonaName(
				analysis?.persona_name ?? analysis?.personaName ?? ""
			);
		});
		return () => {
			cancelled = true;
		};
	}, [isOpen, websiteId, analysisId]);

	function handleClose() {
		if (!creating) onClose?.();
	}

	function goCreatePersona() {
		if (creating) return;
		onClose?.();
		const params = new URLSearchParams();
		const returnTo = monitoringAnalysisStrategyReturnPath(analysisId);
		if (returnTo) params.set("returnTo", returnTo);
		const query = params.toString();
		navigate(query ? `${SETTINGS_PAGE}?${query}` : SETTINGS_PAGE);
	}

	async function handleGenerate() {
		if (creating) return;
		setCreating(true);
		setError("");
		const res = await createMonitoringWritingAssistantRun(websiteId, {
			analysisId,
			recommendationRef,
			mode,
			targetUrl,
			personaId: lockedPersonaId
				? lockedPersonaId
				: personaId === ""
					? undefined
					: Number(personaId),
		});
		setCreating(false);
		if (!res.ok || !res.run?.id) {
			setError(res.message || "Could not start the writing run.");
			return;
		}
		onClose?.();
		onCreated?.(res.run.id);
	}

	return (
		<Modal
			isOpen={isOpen}
			toggle={handleClose}
			centered
			size="md"
			className="monitoring__writing-modal"
			backdrop={creating ? "static" : true}
			keyboard={!creating}
		>
			<ModalHeader tag="div" toggle={creating ? undefined : handleClose}>
				<span className="monitoring__writing-modal-title">
					<Sparkles size={18} aria-hidden /> AI Writing Assistant
				</span>
				{title && <span className="monitoring__writing-modal-desc">{title}</span>}
			</ModalHeader>
			<ModalBody className="monitoring__writing-modal-body">
				{lockedPersonaId ? (
					<>
						<p className="monitoring__writing-modal-lead">
							This analysis was run for a persona — the draft uses the same one.
						</p>
						<div>
							<Label className="monitoring__writing-modal-label">Persona</Label>
							<div className="monitoring__writing-modal-locked-persona">
								{(() => {
									const match = personas.find(
										(p) => Number(p.id) === lockedPersonaId
									);
									if (match) {
										return `${match.name}${
											match.profession && match.profession !== match.name
												? ` · ${match.profession}`
												: ""
										}`;
									}
									return lockedPersonaName || `Persona #${lockedPersonaId}`;
								})()}
							</div>
						</div>
					</>
				) : (
					<>
						<p className="monitoring__writing-modal-lead">
							Pick a persona to write for — this can’t be changed once generation
							starts.
						</p>
						<div>
							<Label
								for="monitoring-writing-persona"
								className="monitoring__writing-modal-label"
							>
								Persona
							</Label>
							<Input
								id="monitoring-writing-persona"
								type="select"
								className="monitoring__writing-modal-select"
								value={personaId}
								onChange={(e) => setPersonaId(e.target.value)}
								disabled={loading || creating}
							>
								<option value="">No specific persona</option>
								{personas.map((persona) => (
									<option key={persona.id} value={persona.id}>
										{persona.name}
										{persona.profession && persona.profession !== persona.name
											? ` · ${persona.profession}`
											: persona.tone
												? ` — ${persona.tone}`
												: ""}
									</option>
								))}
							</Input>
							{!loading && personas.length === 0 && !error ? (
								<p className="monitoring__writing-modal-hint">
									No personas set up yet — generation will use a neutral voice unless
									you create one.
								</p>
							) : null}
							<Button
								type="button"
								color="link"
								className="monitoring__writing-modal-create-persona"
								onClick={goCreatePersona}
								disabled={loading || creating}
							>
								<Plus size={14} aria-hidden className="me-1" />
								Create new persona
							</Button>
						</div>
					</>
				)}
				{error && <p className="monitoring__writing-modal-error">{error}</p>}
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" outline onClick={handleClose} disabled={creating}>
					Cancel
				</Button>
				<Button color="primary" onClick={handleGenerate} disabled={loading || creating}>
					{creating && <Spinner size="sm" className="me-2" />}
					Generate
				</Button>
			</ModalFooter>
		</Modal>
	);
}
